import * as basedef from "./Interface";
import { ConnectorOptions } from "./Interface";
import { Subject, fromEvent, Observable } from "rxjs";
import { takeUntil } from "rxjs/operators";

type MessageListener = (msg: basedef.Message, envelope?: basedef.Envelope<basedef.Message>) => void;
type EnvelopeListener = (envelope: basedef.Envelope<basedef.Message>) => void;

/**
 * Shared infrastructure for all postMessage-based connectors.
 * Subclasses declare their name, protocol version, and advertised message types,
 * and implement handleIncomingData for domain-specific inbound parsing.
 */
export abstract class PostMessageConnectorBase implements basedef.Connector {

    protected abstract readonly name: string;
    protected abstract readonly protocolVersion: number;

    /** Message types this connector advertises in its InitResponse. */
    protected abstract readonly advertisedMessageTypes: ReadonlyArray<{ type: string; version: number }>;

    /** Handle a raw inbound data payload (already origin-validated). */
    protected abstract handleIncomingData(data: unknown): void;

    /** Override to handle outbound domain messages (non-Init). Default logs a warning. */
    protected sendDomainMessage(message: basedef.Message): void {
        console.warn(`[${this.name}]: Unexpected outbound message type, ignoring. Message was:`, message);
    }

    private options?: ConnectorOptions;
    private readonly raw$ = new Subject<basedef.Envelope<basedef.Message>>();
    private readonly messageMap: { [type: string]: Subject<basedef.Envelope<basedef.Message>> } = {};
    private readonly destroy$ = new Subject<void>();
    private readonly postMessage$: Observable<MessageEvent> = fromEvent(window, "message") as Observable<MessageEvent>;
    private sendToWindow?: Window;

    /** Post arbitrary data to the current target origin. */
    protected postToTarget(data: unknown): void {
        this.sendToWindow?.postMessage(data, this.options?.targetOrigin || '');
    }

    init(settings: any): void {
        this.options = settings as ConnectorOptions;
        this.postMessage$.pipe(takeUntil(this.destroy$)).subscribe((ev) => this.postMessageListener(ev));
        this.sendToWindow = window.opener || window.parent;
        if (!this.sendToWindow) {
            console.warn(`[${this.name}]: neither opener nor parent present, falling back to sending message to same window. This is likely not intended`);
            this.sendToWindow = window;
        }
    }

    send(message: basedef.Message): void {
        switch (message.constructor) {
            case basedef.InitMessage:
                this.handleInitMessage(message as basedef.InitMessage);
                break;
            case basedef.InitRejectedMessage:
                console.warn(`[${this.name}]: Initialization rejected by other party: ` + (message as basedef.InitRejectedMessage).reason);
                this.close();
                break;
            default:
                this.sendDomainMessage(message);
        }
    }

    onMessage(type: string, callback: MessageListener): void {
        const subj = this.messageMap[type] ?? new Subject<basedef.Envelope<basedef.Message>>();
        this.messageMap[type] = subj;
        subj.pipe(takeUntil(this.destroy$)).subscribe((e) => callback(e.payload, e));
    }

    onReceived(callback: EnvelopeListener): void {
        this.raw$.pipe(takeUntil(this.destroy$)).subscribe(callback);
    }

    close(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.raw$.complete();
        Object.values(this.messageMap).forEach(m => m.complete());
    }

    protected notifyListeners(envelope: basedef.Envelope<basedef.Message>): void {
        this.raw$.next(envelope);
        const subj = this.messageMap[envelope.type];
        if (subj) subj.next(envelope);
    }

    protected envelope(msg: basedef.Message): basedef.Envelope<basedef.Message> {
        return {
            protocolVersion: this.protocolVersion,
            type: (msg.constructor as typeof basedef.Message).type,
            payload: msg,
            source: this.name,
        };
    }

    private handleInitMessage(msg: basedef.InitMessage): void {
        if (!msg.supportedProtocolVersions?.includes(this.protocolVersion)) {
            console.warn(`[${this.name}]: Other party does not support our protocol version ${this.protocolVersion}`);
            this.notifyListeners(this.envelope(new basedef.InitRejectedMessage("Unsupported protocol version")));
        }
        const supportedTypes = [
            basedef.InitMessage.type,
            basedef.InitResponseMessage.type,
            basedef.InitRejectedMessage.type,
            ...this.advertisedMessageTypes.map(t => t.type),
        ];
        const unsupportedMessages = Object.keys(msg.requestingMessageTypes)
            .filter(t => !supportedTypes.includes(t));
        if (unsupportedMessages.length > 0) {
            console.warn(`[${this.name}]: Other party requests unsupported message types`, unsupportedMessages);
            this.notifyListeners(this.envelope(new basedef.InitRejectedMessage("Unsupported message types: " + unsupportedMessages.join(", "))));
        }
        const response = new basedef.InitResponseMessage(this.protocolVersion);
        this.advertisedMessageTypes.forEach(({ type, version }) => {
            response.offeredMessageTypes[type] = [version];
        });
        this.notifyListeners(this.envelope(response));
    }

    private postMessageListener(postMsg: MessageEvent): void {
        if (!this.options?.allowedOrigins?.includes(postMsg.origin)) {
            console.debug(`[${this.name}]: Received message from unallowed origin: ` + postMsg.origin);
            return;
        }
        this.options = {
            allowedOrigins: this.options.allowedOrigins,
            targetOrigin: postMsg.origin,
        };
        this.handleIncomingData(postMsg.data);
    }
}
